import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useBoolState } from '../../../scripts/hooks';
import { UIIconButton } from '../ui/UIIconButton/UIIconButton';

interface CarouselProps {
  children: React.ReactNode;
  disableAutoScroll?: boolean;
  hideArrows?: boolean;
  className?: string;
}

export const Carousel: React.FC<CarouselProps> = ({
  children,
  disableAutoScroll,
  hideArrows,
  className,
}) => {
  const [isHovering, setIsHovering, setIsNotHovering] = useBoolState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const offset = disableAutoScroll ? 1000 : 200;

  useEffect(() => {
    if (disableAutoScroll) {
      return;
    }

    let animationFrameId: number;

    const smoothScroll = () => {
      if (!isHovering && scrollRef.current) {
        scrollRef.current.scrollLeft += 1;
        animationFrameId = requestAnimationFrame(smoothScroll);
      }
    };

    smoothScroll();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [disableAutoScroll, isHovering]);

  const scroll = (scrollOffset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: scrollRef.current.scrollLeft + scrollOffset,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div
      className={classNames('relative flex gap-4 items-center', className)}
      onMouseEnter={setIsHovering}
      onMouseLeave={setIsNotHovering}
    >
      {!hideArrows && (
        <UIIconButton
          className="absolute -left-6 z-10"
          name="arrow-left"
          onClick={() => {
            scroll(-offset);
          }}
        />
      )}
      <div className="flex overflow-hidden" ref={scrollRef}>
        {children}
      </div>
      {!hideArrows && (
        <UIIconButton
          className="absolute -right-6 z-10"
          name="arrow-right"
          onClick={() => {
            scroll(offset);
          }}
        />
      )}
    </div>
  );
};
